// ไฟล์: frontend/src/pages/DashboardPage.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import "./DashboardPage.css";

// ลงทะเบียน ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DashboardPage = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get("/dashboard");
        setDashboardData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle"></i>{" "}
            เกิดข้อผิดพลาดในการโหลดข้อมูลแดชบอร์ด
          </div>
        </div>
      </div>
    );
  }

  // ข้อมูลสำหรับกราฟการเข้าชมรายวัน
  const dailyVisitData = {
    labels: dashboardData.visitStats.daily.map((item) => {
      const date = new Date(item.visit_date);
      return date.toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
      });
    }),
    datasets: [
      {
        label: "จำนวนการเข้าชม",
        data: dashboardData.visitStats.daily.map((item) => item.total),
        borderColor: "#3490dc",
        backgroundColor: "rgba(52, 144, 220, 0.2)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // ข้อมูลสำหรับกราฟการเข้าชมรายเดือน
  const monthlyVisitData = {
    labels: dashboardData.visitStats.monthly.map((item) => {
      const date = new Date(item.year, item.month - 1);
      return date.toLocaleDateString("th-TH", {
        month: "short",
        year: "numeric",
      });
    }),
    datasets: [
      {
        label: "จำนวนการเข้าชม",
        data: dashboardData.visitStats.monthly.map((item) => item.total),
        backgroundColor: "#38c172",
        borderRadius: 5,
      },
    ],
  };

  // ตัวเลือกสำหรับกราฟ
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="dashboard-page admin-dashboard">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-tachometer-alt"></i> แดชบอร์ดผู้ดูแลระบบ
          </h1>
          <div className="page-actions">
            <Link to="/admin/users" className="btn btn-primary">
              <i className="fas fa-users"></i> จัดการผู้ใช้
            </Link>
            <Link to="/admin/resources" className="btn btn-info">
              <i className="fas fa-check-circle"></i> อนุมัติสื่อ
            </Link>
            <Link to="/admin/create-news" className="btn btn-secondary">
              <i className="fas fa-newspaper"></i> สร้างข่าวสาร
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card primary">
            <i className="fas fa-users"></i>
            <div className="stat-content">
              <div className="stat-value">{dashboardData.counts.users}</div>
              <div className="stat-label">ครูในระบบ</div>
            </div>
          </div>
          <div className="stat-card success">
            <i className="fas fa-book"></i>
            <div className="stat-content">
              <div className="stat-value">{dashboardData.counts.resources}</div>
              <div className="stat-label">สื่อการสอน</div>
            </div>
          </div>
          <div className="stat-card warning">
            <i className="fas fa-lightbulb"></i>
            <div className="stat-content">
              <div className="stat-value">
                {dashboardData.counts.innovations}
              </div>
              <div className="stat-label">นวัตกรรม</div>
            </div>
          </div>
          <div className="stat-card info">
            <i className="fas fa-pen-fancy"></i>
            <div className="stat-content">
              <div className="stat-value">{dashboardData.counts.articles}</div>
              <div className="stat-label">บทความ</div>
            </div>
          </div>
        </div>

        {/* Visit Stats */}
        <div className="dashboard-grid">
          <div className="chart-container">
            <div className="chart-header">
              <h2 className="chart-title">
                <i className="fas fa-chart-line"></i> สถิติการเข้าชมรายวัน
              </h2>
              <div className="chart-info">
                <span className="badge badge-primary">
                  {dashboardData.counts.todayVisits} ครั้ง
                </span>{" "}
                วันนี้
              </div>
            </div>
            <div className="chart-body">
              <Line data={dailyVisitData} options={chartOptions} />
            </div>
          </div>

          <div className="dashboard-sidebar">
            <div className="card">
              <div className="card-header">
                <i className="fas fa-calendar-alt"></i> สถิติรายเดือน
              </div>
              <div className="card-body">
                <div className="sidebar-stat">
                  <span className="sidebar-stat-label">เดือนนี้</span>
                  <span className="sidebar-stat-value">
                    <i className="fas fa-user"></i>{" "}
                    {dashboardData.counts.monthlyVisits} ครั้ง
                  </span>
                </div>
                <div className="sidebar-chart">
                  <Bar
                    data={monthlyVisitData}
                    options={{
                      ...chartOptions,
                      plugins: { legend: { display: false } },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Popular Resources */}
        <div className="card mt-4">
          <div className="card-header">
            <i className="fas fa-star"></i> สื่อการสอนยอดนิยม
          </div>
          <div className="card-body">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>#</th>
                    <th style={{ width: "40%" }}>ชื่อสื่อการสอน</th>
                    <th style={{ width: "20%" }}>ผู้สร้าง</th>
                    <th style={{ width: "10%" }}>เข้าชม</th>
                    <th style={{ width: "10%" }}>ดาวน์โหลด</th>
                    <th style={{ width: "10%" }}>คะแนน</th>
                    <th style={{ width: "5%" }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.popularResources.map((resource, index) => (
                    <tr key={resource.id}>
                      <td>{index + 1}</td>
                      <td>
                        <Link to={`/resources/${resource.id}`}>
                          {resource.title}
                        </Link>
                      </td>
                      <td>{resource.author_name}</td>
                      <td>{resource.view_count}</td>
                      <td>{resource.download_count}</td>
                      <td>
                        <div className="rating">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i
                              key={star}
                              className={`fas fa-star ${
                                star <=
                                Math.round(Number(resource.avg_rating) || 0)
                                  ? "filled"
                                  : ""
                              }`}
                            ></i>
                          ))}
                          <span className="rating-value">
                            {resource.avg_rating &&
                            !isNaN(Number(resource.avg_rating))
                              ? Number(resource.avg_rating).toFixed(1)
                              : "0.0"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/resources/${resource.id}`}
                          className="btn btn-sm btn-primary"
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top Teachers */}
        <div className="card mt-4">
          <div className="card-header">
            <i className="fas fa-user-graduate"></i> ครูผู้แบ่งปันยอดเยี่ยม
          </div>
          <div className="card-body">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>#</th>
                    <th style={{ width: "20%" }}>ชื่อ-นามสกุล</th>
                    <th style={{ width: "30%" }}>โรงเรียน</th>
                    <th style={{ width: "15%" }}>จำนวนผลงาน</th>
                    <th style={{ width: "15%" }}>ผู้ติดตาม</th>
                    <th style={{ width: "15%" }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.topTeachers.map((teacher, index) => (
                    <tr key={teacher.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="user-info">
                          <img
                            src={
                              teacher.profile_image
                                ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${teacher.profile_image}`
                                : `https://ui-avatars.com/api/?name=${teacher.fullname}&background=random`
                            }
                            alt={teacher.fullname}
                            className="avatar-sm mr-2"
                          />
                          <span>{teacher.fullname}</span>
                        </div>
                      </td>
                      <td>{teacher.school}</td>
                      <td>{teacher.content_count}</td>
                      <td>{teacher.follower_count}</td>
                      <td>
                        <div className="btn-group">
                          <Link
                            to={`/teachers/${teacher.id}`}
                            className="btn btn-sm btn-info"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          <button className="btn btn-sm btn-primary">
                            <i className="fas fa-envelope"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
